import { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { ProjectCategory, FilterState } from '../types';

interface FilterBarProps {
  categories: ProjectCategory[];
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
}

const FilterBar = ({ categories, filters, onFilterChange }: FilterBarProps) => {
  const [searchValue, setSearchValue] = useState(filters.searchQuery);

  const handleCategoryClick = (categoryId: string) => {
    onFilterChange({
      ...filters,
      category: filters.category === categoryId ? 'all' : categoryId,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    onFilterChange({
      ...filters,
      searchQuery: value,
    });
  };

  const handleClearFilters = () => {
    setSearchValue('');
    onFilterChange({
      category: 'all',
      searchQuery: '',
    });
  };

  const hasActiveFilters = filters.category !== 'all' || filters.searchQuery !== '';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="w-full sm:w-96">
          <Input
            type="search"
            placeholder="Search projects..."
            value={searchValue}
            onChange={handleSearchChange}
            className="w-full bg-blue-50"
          />
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            iconPosition="left"
            iconSize={16}
            onClick={handleClearFilters}
            className="text-secondary hover:text-primary"
          >
            Clear Filters
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 justify-center">
        {categories.map((category) => {
          const isActive = filters.category === category.id;
          return (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                isActive
                  ? 'bg-accent text-white shadow-brand'
                : 'bg-surface text-secondary hover:bg-accent/10 hover:text-accent'
              }`}
            >
              <span className="flex items-center gap-2">
                {category.label}
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    isActive
                    ? 'bg-white/20 text-white' : 'bg-muted text-secondary'
                  }`}
                >
                  {category.count}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FilterBar;